/**
 * W66.X22 — Jumpstart frontend rebuild (gap-math six-row spine, 30/60/90 cadence)
 *
 * Replaces the rejected W66.X20 seeded role-template content and the fake
 * "1/13 complete" progress bar with live gap math from the compute_jumpstart()
 * server function (W66.X21).
 *
 * A Jumpstart is the 90-day front-end accelerator on top of the annual sales plan.
 * Its only job: get a rep hitting their plan running by Day 90. The page derives
 * the six-row spine and the 30/60/90 cadence live from quota + pipeline + canon
 * stage probabilities — nothing is stamped or seeded.
 *
 * Data: supabase.rpc('compute_jumpstart', { p_user_id, p_tenant_id: getCurrentTenant()?.id ?? 1 }).
 *
 * Return shape (W66.X21):
 *   { rep_id, rep_name, status, computed_at, inputs,
 *     rows: [{ key, label, unit('multiple'|'usd'|'count'), have, need, gap_value,
 *              band('Green'|'Yellow'|'Orange'|'Red'), day_target?, cadence? }],
 *     stages: [{ cp, required_count, current_count, gap, by_day }] }
 *   status 'needs_input' / 'not_found' carry an `instruction` string instead of rows.
 *   The 30/60/90 cadence lives on the gap_close row: rows[gap_close].cadence.day_30/60/90.
 *
 * Four states: loading, error, needs_input (avg_deal_size null), populated.
 *
 * Route: /jumpstart (AuthGuard, all roles). Multi-tenant.
 * Bryan K. Short, Founder and CEO, Mi6 Group.
 */

import { useEffect, useState, useCallback } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { supabase, getSession, getCurrentTenant } from "@/lib/session";
import { getImpersonationTarget, subscribe as subscribeImpersonation } from "@/lib/impersonation";

// ─── Canon 5-color palette + neutrals (CANON_VISUALS_LOCKED.md §5, §12) ───────
const C = {
  gray:   "#6B7280",
  red:    "#DC2626",
  orange: "#EA580C",
  yellow: "#EAB308",
  green:  "#16A34A",
} as const;

const N = {
  surface:     "#F9FAFB",
  panel:       "#FFFFFF",
  border:      "#E5E7EB",
  textPrimary: "#111827",
  textMuted:   C.gray,
  redSoft:     "#FEF2F2",
  redBorder:   "#FECACA",
} as const;

/** Server band (capitalized) → canon color. Gray reserved for no-data. */
function bandColor(band: string | undefined, gapValue: number, need: number): string {
  switch ((band || "").toLowerCase()) {
    case "green":  return C.green;
    case "yellow": return C.yellow;
    case "orange": return C.orange;
    case "red":    return C.red;
    case "gray":   return C.gray;
  }
  // Fallback if band missing: derive from gap fraction (spec v2 thresholds).
  if (need <= 0 || gapValue <= 0) return C.green;
  const pct = gapValue / need;
  if (pct > 0.5) return C.red;
  if (pct > 0.2) return C.orange;
  return C.yellow;
}

// ─── Effective rep (impersonation-aware) ───────────────────────────────────────

interface EffectiveRep {
  id: string;
  name: string;
  role: string;
}

function effectiveRep(): EffectiveRep {
  const t = getImpersonationTarget();
  if (t) return { id: t.id, name: t.name, role: t.role };
  const s = getSession();
  return { id: s?.id ?? "", name: s?.name ?? "", role: s?.role ?? "" };
}

function useEffectiveRep(): EffectiveRep {
  const [rep, setRep] = useState<EffectiveRep>(effectiveRep);
  useEffect(() => subscribeImpersonation(() => setRep(effectiveRep())), []);
  return rep;
}

/** Split "Bryan K. Short" → { first: "Bryan", last: "K. Short" }. */
function splitName(full: string): { first: string; last: string } {
  const parts = (full || "").trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === "") return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

const ROLE_LABEL: Record<string, string> = {
  ae: "AE",
  manager: "Manager",
  bdr: "BDR",
  marketing: "Marketing",
  executive: "Executive",
  se: "SE",
};

function roleLabel(role: string): string {
  return ROLE_LABEL[role] ?? (role ? role.toUpperCase() : "");
}

// ─── Formatting ─────────────────────────────────────────────────────────────

type ServerUnit = "multiple" | "usd" | "count" | string;

function fmtUsd(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs % 1_000_000 === 0 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}k`;
  return `${sign}$${Math.round(abs)}`;
}

function fmtMultiple(n: number): string {
  return `${n.toFixed(n >= 10 ? 0 : n >= 1 ? 1 : 3)}x`;
}

function fmtCount(n: number): string {
  const r = Math.round(n);
  return `${r} ${r === 1 ? "account" : "accounts"}`;
}

function fmtValue(value: number, unit: ServerUnit): string {
  switch (unit) {
    case "usd": return fmtUsd(value);
    case "multiple": return fmtMultiple(value);
    case "count": return fmtCount(value);
    default: return String(value);
  }
}

// ─── Server payload types (W66.X21 compute_jumpstart) ─────────────────────────

interface ServerRow {
  key: string;
  label: string;
  unit: ServerUnit;
  have: number;
  need: number;
  gap_value: number;
  band?: string;
  day_target?: number;
  cadence?: {
    day_30?: CadenceCell;
    day_60?: CadenceCell;
    day_90?: CadenceCell;
  };
}

interface CadenceCell {
  window: string;   // "CP1-CP3"
  have: number;
  need: number;
  gap: number;
}

interface ServerPayload {
  rep_id?: string;
  rep_name?: string;
  status: "ok" | "needs_input" | "not_found" | string;
  instruction?: string;
  computed_at?: string;
  rows?: ServerRow[];
}

// ─── Header ─────────────────────────────────────────────────────────────────

function JumpstartHeader({ rep, serverName }: { rep: EffectiveRep; serverName?: string }) {
  const displayName = serverName || rep.name;
  const { first, last } = splitName(displayName);
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const repTitle = [first, last].filter(Boolean).join(" ") || "Jumpstart";
  return (
    <div style={{ marginBottom: "1.25rem" }}>
      <h2 style={{ margin: 0, color: N.textPrimary, fontSize: "1.4rem", fontWeight: 700, lineHeight: 1.15 }}>
        {repTitle} — JUMPSTART
      </h2>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.35rem", color: N.textMuted, fontSize: "0.82rem" }}>
        {rep.role && <span style={{ fontWeight: 600 }}>{roleLabel(rep.role)}</span>}
        {rep.role && <span aria-hidden="true">·</span>}
        <span>{today}</span>
      </div>
    </div>
  );
}

// ─── Six-row spine ────────────────────────────────────────────────────────────

const SUBTEXT: Record<string, string> = {
  coverage:       "Pipeline value as a multiple of quota. Gap shown as the dollar shortfall to reach target coverage.",
  pipeline_depth: "Total open pipeline ARR versus required at target coverage.",
  hunt:           "New accounts to put into early stages.",
  qualify:        "Accounts to qualify and advance.",
  advance:        "Accounts to move through the middle of the path.",
  gap_close:      "Total accounts to move over 90 days, broken out by 30 / 60 / 90 below.",
};

function GapBadge({ row, color }: { row: ServerRow; color: string }) {
  const gap = Number(row.gap_value ?? 0);
  if (gap <= 0) {
    const exceeding = Number(row.have) > Number(row.need);
    return (
      <span style={{ fontSize: "0.72rem", fontWeight: 700, color: C.green, border: `1px solid ${C.green}`, borderRadius: 9999, padding: "0.12rem 0.55rem", whiteSpace: "nowrap" }}>
        {exceeding ? "Exceeding" : "At target"}
      </span>
    );
  }
  // Coverage reports its gap as a dollar shortfall; everything else uses the row unit.
  const gapUnit: ServerUnit = row.key === "coverage" ? "usd" : row.unit;
  return (
    <span style={{ fontSize: "0.72rem", fontWeight: 700, color, border: `1px solid ${color}`, borderRadius: 9999, padding: "0.12rem 0.55rem", whiteSpace: "nowrap" }}>
      {fmtValue(gap, gapUnit)} short
    </span>
  );
}

function SpineRowView({ row }: { row: ServerRow }) {
  const color = bandColor(row.band, Number(row.gap_value ?? 0), Number(row.need ?? 0));
  const subtext = SUBTEXT[row.key];
  return (
    <div
      style={{
        background: N.panel,
        border: `1px solid ${N.border}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 10,
        padding: "0.85rem 1.1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        flexWrap: "wrap",
      }}
      data-testid={`jumpstart-spine-${row.key}`}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, color: N.textMuted, letterSpacing: "0.04em", textTransform: "uppercase" }}>
          {row.label}
        </div>
        {subtext && (
          <div style={{ fontSize: "0.78rem", color: N.textMuted, marginTop: "0.25rem", lineHeight: 1.4, maxWidth: 470 }}>
            {subtext}
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.9rem", flexShrink: 0 }}>
        <div style={{ textAlign: "right" }}>
          <span style={{ fontSize: "1.45rem", fontWeight: 700, color: N.textPrimary }}>
            {fmtValue(Number(row.have ?? 0), row.unit)}
          </span>
          <span style={{ fontSize: "0.82rem", color: N.textMuted, marginLeft: "0.4rem" }}>
            of {fmtValue(Number(row.need ?? 0), row.unit)}
          </span>
        </div>
        <GapBadge row={row} color={color} />
      </div>
    </div>
  );
}

function SpineList({ rows }: { rows: ServerRow[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginBottom: "1.75rem" }}>
      {rows.map((r) => <SpineRowView key={r.key} row={r} />)}
    </div>
  );
}

// ─── 30/60/90 cadence section ───────────────────────────────────────────────

interface CadenceBlock {
  day: 30 | 60 | 90;
  label: string;     // "Move CP1-3"
  have: number;
  need: number;
  gap: number;
}

/** Pull the 30/60/90 cadence off the gap_close row. */
function cadenceFromRows(rows: ServerRow[]): CadenceBlock[] {
  const gapClose = rows.find((r) => r.key === "gap_close");
  const c = gapClose?.cadence;
  if (!c) return [];
  const label = (d: 30 | 60 | 90) => (d === 30 ? "Move CP1-3" : d === 60 ? "Move CP4-6" : "Move CP7-10");
  const mk = (d: 30 | 60 | 90, cell?: CadenceCell): CadenceBlock | null => {
    if (!cell) return null;
    const need = Number(cell.need ?? 0);
    const have = Number(cell.have ?? 0);
    return { day: d, label: label(d), have, need, gap: cell.gap != null ? Number(cell.gap) : Math.max(0, need - have) };
  };
  return [mk(30, c.day_30), mk(60, c.day_60), mk(90, c.day_90)].filter((b): b is CadenceBlock => b !== null);
}

function CadenceSection({ blocks }: { blocks: CadenceBlock[] }) {
  if (!blocks || blocks.length === 0) return null;
  return (
    <div>
      <h3 style={{ fontSize: "0.82rem", fontWeight: 700, color: N.textMuted, letterSpacing: "0.05em", textTransform: "uppercase", margin: "0 0 0.75rem" }}>
        30 / 60 / 90
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.75rem" }}>
        {blocks.map((b) => {
          const accent = bandColor(undefined, b.gap, b.need);
          return (
            <div
              key={b.day}
              style={{ background: N.panel, border: `1px solid ${N.border}`, borderTop: `3px solid ${accent}`, borderRadius: 10, padding: "0.9rem 1.1rem" }}
              data-testid={`jumpstart-cadence-${b.day}`}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.92rem", fontWeight: 700, color: N.textPrimary }}>Day {b.day}</span>
                <span style={{ fontSize: "0.74rem", fontWeight: 600, color: N.textMuted }}>{b.label}</span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", marginTop: "0.6rem" }}>
                <span style={{ fontSize: "1.35rem", fontWeight: 700, color: N.textPrimary }}>{Math.round(b.have)}</span>
                <span style={{ fontSize: "0.8rem", color: N.textMuted }}>of {Math.round(b.need)}</span>
              </div>
              <div style={{ fontSize: "0.76rem", fontWeight: 700, color: accent, marginTop: "0.35rem" }}>
                {b.gap > 0 ? `${Math.round(b.gap)} to move` : "On pace"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Needs-input state ────────────────────────────────────────────────────────

function NeedsInputCard({ repId, message, onSaved }: { repId: string; message: string; onSaved: () => void }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    const num = Number(String(value).replace(/[^0-9.]/g, ""));
    if (!num || num <= 0) { setErr("Enter your running average deal size in dollars."); return; }
    setSaving(true); setErr(null);
    try {
      // Update the rep's existing quota-plan row. If none exists yet, insert one.
      const sb = supabase();
      const { data: existing, error: selErr } = await sb
        .from("rep_quota_plan")
        .select("user_id")
        .eq("user_id", repId)
        .limit(1);
      if (selErr) throw new Error(selErr.message);

      if (existing && existing.length > 0) {
        const { error } = await sb
          .from("rep_quota_plan")
          .update({ avg_deal_size: num })
          .eq("user_id", repId);
        if (error) throw new Error(error.message);
      } else {
        const { error } = await sb
          .from("rep_quota_plan")
          .insert({ user_id: repId, tenant_id: getCurrentTenant()?.id ?? 1, avg_deal_size: num });
        if (error) throw new Error(error.message);
      }
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: N.panel, border: `1px solid ${N.border}`, borderRadius: 10, padding: "1.4rem 1.5rem", maxWidth: 520 }}>
      <p style={{ margin: "0 0 1rem", color: N.textPrimary, fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.5 }}>
        {message}
      </p>
      <label htmlFor="avg-deal-size" style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: N.textMuted, marginBottom: "0.3rem" }}>
        Your running average deal size (USD)
      </label>
      <input
        id="avg-deal-size"
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="e.g. 75000"
        data-testid="jumpstart-avg-deal-size"
        style={{ width: "100%", background: N.surface, border: `1px solid ${N.border}`, borderRadius: 7, color: N.textPrimary, padding: "0.55rem 0.75rem", fontSize: "0.95rem", boxSizing: "border-box", marginBottom: "0.9rem" }}
      />
      <button
        onClick={save}
        disabled={saving}
        data-testid="jumpstart-save-deal-size"
        style={{ background: C.green, color: "#FFFFFF", border: "none", borderRadius: 7, padding: "0.55rem 1.4rem", fontSize: "0.9rem", fontWeight: 700, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.6 : 1 }}
      >
        {saving ? "Saving" : "Save"}
      </button>
      {err && (
        <div style={{ marginTop: "0.8rem", color: C.red, background: N.redSoft, border: `1px solid ${N.redBorder}`, borderRadius: 6, padding: "0.5rem 0.7rem", fontSize: "0.82rem" }}>
          {err}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "needs_input"; message: string }
  | { kind: "ready"; rows: ServerRow[]; cadence: CadenceBlock[]; serverName?: string };

export default function JumpstartPage() {
  const rep = useEffectiveRep();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  const load = useCallback(async () => {
    if (!rep.id) { setState({ kind: "error", message: "No active session. Sign in to see your Jumpstart." }); return; }
    setState({ kind: "loading" });
    try {
      const { data, error } = await supabase().rpc("compute_jumpstart", {
        p_user_id: rep.id,
        p_tenant_id: getCurrentTenant()?.id ?? 1,
      });
      if (error) {
        const msg = /compute_jumpstart.*does not exist|could not find the function/i.test(error.message)
          ? "Gap math is not available yet. The compute_jumpstart server function is still deploying."
          : error.message;
        setState({ kind: "error", message: msg });
        return;
      }

      const payload: ServerPayload = (Array.isArray(data) ? data[0] : data) ?? { status: "not_found" };

      if (payload.status === "needs_input") {
        setState({ kind: "needs_input", message: payload.instruction || "Set your average deal size to see your gap math." });
        return;
      }
      if (payload.status === "not_found") {
        // Manager role may not have a quota plan — surface a clear, role-aware message
        const isManager = getSession()?.role === "manager";
        const fallback = isManager
          ? "Jumpstart gap math is based on rep quota plans. Select a rep via the impersonation menu to view their Jumpstart."
          : "No Jumpstart data found for this rep. Ensure a quota plan has been set.";
        setState({ kind: "error", message: payload.instruction || fallback });
        return;
      }

      const rows = Array.isArray(payload.rows) ? payload.rows : [];
      if (rows.length === 0) {
        setState({ kind: "error", message: "Gap math returned no rows. Check your quota and average deal size." });
        return;
      }
      setState({ kind: "ready", rows, cadence: cadenceFromRows(rows), serverName: payload.rep_name });
    } catch (e: any) {
      setState({ kind: "error", message: e?.message ?? "Failed to load your Jumpstart." });
    }
  }, [rep.id]);

  useEffect(() => { load(); }, [load]);

  const serverName = state.kind === "ready" ? state.serverName : undefined;

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Jumpstart" }]}
      showContextTabs={false}
    >
      <div style={{ background: N.surface, minHeight: "100vh", paddingTop: "1.5rem" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 1rem 3rem" }}>
          <JumpstartHeader rep={rep} serverName={serverName} />

          {state.kind === "loading" && (
            <div style={{ color: N.textMuted, padding: "2rem", textAlign: "center" }}>Loading your gap math</div>
          )}

          {state.kind === "error" && (
            <div style={{ color: C.red, background: N.redSoft, border: `1px solid ${N.redBorder}`, borderRadius: 8, padding: "1rem 1.25rem", fontSize: "0.9rem" }}>
              {state.message}
            </div>
          )}

          {state.kind === "needs_input" && (
            <NeedsInputCard repId={rep.id} message={state.message} onSaved={load} />
          )}

          {state.kind === "ready" && (
            <>
              <SpineList rows={state.rows} />
              <CadenceSection blocks={state.cadence} />
            </>
          )}
        </div>
      </div>
    </OperatorShell>
  );
}
